import InviteFilters from 'Components/campaigns/invites/InviteFilters';
import InvitesTable from 'Components/campaigns/invites/InvitesTable';
import FilterIcon from 'Components/icons/FilterIcon';
import useDocumentTitle from 'hooks/useDocumentTitle';
import CampaignLayout from 'Layouts/CampaignLayout';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { defaultInvitesFilters, updateInvitesFilters } from 'slices/campaigns/invites/reducer';
import { fetchCampaignInvitesThunk } from 'slices/campaigns/invites/thunk';
import { AppDispatch, RootState } from 'store';
import { CampaignInvitesFilters } from 'types/campaign/invites';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';

const InvitedCreators = () => {
    useDocumentTitle('Invites - Campaign');

    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const queryFilters = searchParams.get('filters');
    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters) : defaultInvitesFilters;
        dispatch(updateInvitesFilters({ ...decodedFilters, page: 1 }));
    }, [queryFilters, dispatch]);

    const filters = useSelector((state: RootState) => state.campaignInvites.filters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const invitesData = useSelector(
        (state: RootState) => state.campaignInvites.campaigns[campaignId!]?.filtered_lists?.[filterId]
    );
    const invites = invitesData?.list ?? [];
    const page = invitesData?.page ?? 1;
    const hasMore = invitesData?.has_more ?? false;
    const count = invitesData?.count ?? 0;

    const loading = useSelector((state: RootState) => state.campaignInvites.loading);
    const error = useSelector((state: RootState) => state.campaignInvites.error);

    useEffect(() => {
        if (!campaignId) return;

        if (filters.page === 1) {
            setInitialLoading(true);
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(
                fetchCampaignInvitesThunk({
                    campaignId,
                    filters: { ...filters, page: filters.page ?? 1 },
                })
            )
                .finally(() => {
                    setFilterId(generateUniqueKey(filters, ['page']));
                    setInitialLoading(false);
                });
        }, 500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [campaignId, dispatch, filters]);

    const setFilters = (updatedFilters: CampaignInvitesFilters) => {
        setSearchParams({ filters: encodeBase64(updatedFilters) });
    };

    const nextPage = () => {
        if (hasMore && campaignId && !loading && invites.length > 0) {
            dispatch(
                fetchCampaignInvitesThunk({
                    campaignId,
                    filters: { ...filters, page: page + 1 },
                })
            );
        }
    };

    return (
        <CampaignLayout
            filters={
                <div className="d-flex justify-content-end align-items-center">
                    <button type="button" className="btn btn-white" onClick={() => showFiltersModal(true)}>
                        <FilterIcon className="me-1" /> Filter
                    </button>
                </div>
            }
        >
            <div className="card campaign-insights-card">
                <div className="card-header d-flex justify-content-between align-items-center p-4 border-0">
                    <p className="campaign-records-count mb-0">
                        {!initialLoading && invites.length > 0
                            ? `Showing ${invites.length} of ${count} Records`
                            : null}
                    </p>
                </div>
                <div className="card-body p-0 m-0">
                    <InvitesTable
                        invites={invites}
                        hasMore={hasMore}
                        loading={loading}
                        initialLoading={initialLoading}
                        error={error}
                        onNextPage={nextPage}
                    />
                </div>
            </div>
            <InviteFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(updatedFilters) => setFilters(updatedFilters)}
            />
        </CampaignLayout>
    );
};

export default InvitedCreators;
